import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Users, Target, Award, Heart } from "lucide-react";
import { scrollToTop } from "../lib/utils";

interface Value {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const QuiSommesNous: React.FC = () => {
  const values: Value[] = [
    {
      icon: <Users className="h-8 w-8" />,
      title: "Équipe Jeune & Dynamique",
      description:
        "Une équipe passionnée qui apporte énergie et innovation à chaque projet.",
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Excellence & Précision",
      description:
        "Nous visons l'excellence dans chaque détail, chaque ligne tracée, chaque fissure réparée.",
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Expertise Reconnue",
      description:
        "Spécialisés dans le marquage au sol, la pose de scellant et la réparation de fissures.",
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Service Client",
      description:
        "La satisfaction totale de nos clients est au cœur de tout ce que nous faisons.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          <img
            src={`${process.env.PUBLIC_URL}/photo-1507003211169-0a1dd7228f2d.jpg`}
            alt="Team working"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge
              variant="outline"
              className="mb-4 text-amber-400 border-amber-400"
            >
              À propos de nous
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Qui sommes-nous ?
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Une équipe jeune et passionnée au service de l'excellence
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 leading-tight">
                Bienvenue chez{" "}
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                  PaveMark
                </span>
              </h2>

              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Bienvenue chez PaveMark, où le{" "}
                  <strong>dynamisme rencontre l'excellence</strong>. Nous sommes
                  une équipe jeune et passionnée, spécialisée dans le marquage
                  au sol, la pose de scellant d'asphalte, et la réparation de
                  fissures.
                </p>

                <p>
                  Chaque jour, nous mettons notre énergie et notre savoir-faire
                  au service de nos clients pour transformer leurs espaces
                  extérieurs et intérieurs en environnements
                  <strong> sûrs, esthétiques et durables</strong>.
                </p>

                <p>
                  Notre engagement envers l'excellence ne se limite pas à la
                  qualité de notre travail, mais s'étend à notre service client.
                  Nous croyons fermement que la clé du succès réside dans la{" "}
                  <strong>satisfaction totale</strong> de ceux qui nous font
                  confiance.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border border-amber-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1621905252507-b35492cc74b4.jpg`}
                      alt="Équipe PaveMark"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Équipe Experte
                    </h4>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl border border-blue-100 mt-8">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1581578731548-c64695cc6952.jpg`}
                      alt="Matériel professionnel"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Équipement Moderne
                    </h4>
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1558618666-fcd25c85cd64.jpg`}
                      alt="Travail de qualité"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Résultats Durables
                    </h4>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-100">
                    <img
                      src={`${process.env.PUBLIC_URL}/photo-1504307651254-35680f356dfd.jpg`}
                      alt="Service client"
                      className="w-full h-32 object-cover rounded-lg mb-4"
                    />
                    <h4 className="font-semibold text-gray-900">
                      Service Premium
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge
              variant="outline"
              className="mb-4 text-amber-600 border-amber-600"
            >
              Nos Valeurs
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ce qui nous distingue
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez les valeurs qui guident notre travail quotidien et notre
              engagement envers l'excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value: Value, index: number) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg"
              >
                <CardContent className="p-8 text-center">
                  <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center text-amber-600 group-hover:from-amber-500 group-hover:to-orange-500 group-hover:text-white transition-all">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-amber-600 transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Notre philosophie
            </h2>

            <div className="space-y-8 text-lg text-gray-600 leading-relaxed">
              <p>
                C'est pourquoi nous prenons le temps de comprendre les besoins
                uniques de chaque projet, afin de livrer des résultats qui{" "}
                <strong>dépassent les attentes</strong>. Chez PaveMark, chaque
                projet est une opportunité de prouver notre expertise et notre
                dévouement. Que vous ayez besoin de marquage pour un
                stationnement ou de scellant pour protéger votre asphalte, notre
                équipe est prête à relever le défi avec{" "}
                <strong>professionnalisme et précision</strong>.
              </p>

              <p>
                Nous sommes fiers de ce que nous faisons, et cela se voit dans
                chaque ligne tracée, chaque fissure réparée, et chaque sourire
                satisfait de nos clients. Faites confiance à une équipe qui
                allie{" "}
                <strong>
                  jeunesse, dynamisme, et une passion inébranlable pour
                  l'excellence
                </strong>
                .
              </p>
            </div>

            <div className="mt-12 p-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Notre engagement
              </h3>
              <p className="text-lg text-gray-700 mb-6 font-medium">
                "Nous nous concentrons sur chaque détail pour que vous puissiez
                vous détendre."
              </p>
              <p className="text-gray-600">
                Contacter nous pour une soumission gratuite, il nous fera
                plaisir de venir vous rencontrer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à découvrir la différence PaveMark ?
          </h2>
          <p className="text-xl text-amber-100 mb-8 leading-relaxed">
            Contactez notre équipe dès aujourd'hui et découvrez pourquoi nos
            clients nous font confiance pour leurs projets de marquage et de
            protection d'asphalte.
          </p>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="bg-white text-amber-600 border-white hover:bg-amber-50 text-lg px-8 py-6 shadow-xl"
          >
            <Link
              to="/contact"
              onClick={() => scrollToTop()}
              className="flex items-center gap-2"
            >
              Rencontrer l'équipe
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default QuiSommesNous;
